import { createContext, useContext, useEffect, useState } from "react";

const CURRENCIES = [
  { code: "USD", symbol: "$",  label: "US Dollar" },
  { code: "MXN", symbol: "$",  label: "Mexican Peso" },
  { code: "EUR", symbol: "€",  label: "Euro" },
];

// Static, coarse conversion table. Deliberately not live rates — we don't want to
// mislead users into thinking these are settled amounts. Real payment currency is
// always the seller's original listing currency; this is a display-only preference.
const RATES_TO_USD = {
  USD: 1.0,
  MXN: 0.055,   // 1 MXN ≈ 0.055 USD (~ Feb 2026)
  EUR: 1.08,    // 1 EUR ≈ 1.08 USD
};

const CurrencyCtx = createContext(null);

export function CurrencyProvider({ children }) {
  const [code, setCode] = useState(() => {
    try {
      const saved = localStorage.getItem("hoomanz_currency");
      if (saved && RATES_TO_USD[saved]) return saved;
    } catch {}
    // Auto-detect from locale on first visit
    try {
      const lang = (navigator.language || "").toLowerCase();
      if (lang.includes("mx") || lang.startsWith("es-mx")) return "MXN";
      if (lang.startsWith("es") || lang.startsWith("de") || lang.startsWith("fr")
          || lang.startsWith("it") || lang.startsWith("pt") || lang.startsWith("nl")) return "EUR";
    } catch {}
    return "USD";
  });

  useEffect(() => { try { localStorage.setItem("hoomanz_currency", code); } catch {} }, [code]);

  // Convert `amount` from `fromCode` into the currently selected display currency.
  const convert = (amount, fromCode = "USD") => {
    const from = RATES_TO_USD[fromCode] ?? 1.0;
    const to = RATES_TO_USD[code] ?? 1.0;
    return (Number(amount) || 0) * from / to;
  };

  const format = (amount, fromCode = "USD", opts = {}) => {
    const value = convert(amount, fromCode);
    const decimals = opts.decimals ?? (value >= 100 ? 0 : 2);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency", currency: code,
        maximumFractionDigits: decimals, minimumFractionDigits: 0,
      }).format(value);
    } catch {
      const sym = CURRENCIES.find(c => c.code === code)?.symbol || "$";
      return `${sym}${value.toFixed(decimals)}`;
    }
  };

  return (
    <CurrencyCtx.Provider value={{ code, setCode, currencies: CURRENCIES, convert, format }}>
      {children}
    </CurrencyCtx.Provider>
  );
}

export function useCurrency() {
  const v = useContext(CurrencyCtx);
  if (!v) throw new Error("useCurrency() outside CurrencyProvider");
  return v;
}

export const SUPPORTED_CURRENCIES = CURRENCIES;
